import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Card, Flex, Text, Grid, AgGridReact, AgGrid } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { useAppContext } from "~/contexts/appContext";
import { LoadingCellRenderer } from "~/pages/Billing/components/LoadingCellRenderer";
import { PAGINATION } from "~/pages/Billing/constants";
import { getRowId, sideBarDef, updateGridDataSource } from "~/pages/Billing/gridUtils";
import { isBillingChecker } from "~/utils/userRoles";
import { CaseDetailsDrawer } from "./CaseDetailsDrawer";
import { CasesGraphqlDataSource } from "./casesGraphqlDataSource";
import { getColumnDefinitions } from "./columnDefinitions";
import { useCaseSlideoutDrawer } from "./hooks/useCaseSlideoutDrawer";
import { billingCasesOverviewCard, billingCasesOverviewHeading } from "./styles_vanilla.css";

const defaultDataSourceFactory = (
  arenaId: number | null = null,
): AgGrid.IServerSideDatasource => new CasesGraphqlDataSource(arenaId);

type CasesOverviewProps = {
  arenaId?: number | null;
  dataSourceFactory?: (arenaId?: number | null) => AgGrid.IServerSideDatasource;
};

export const CasesOverview = ({
  arenaId = null,
  dataSourceFactory = defaultDataSourceFactory,
}: CasesOverviewProps): ReactElement => {
  const [gridApi, setGridApi] = useState<AgGrid.GridApi>();
  const { user } = useAppContext();
  const isChecker = isBillingChecker(user?.roles ?? []);
  const isGpBillingCenterEnabled = useFeatureFlag("showGpBillingCenter", false);
  const colDefs = useMemo(
    () => getColumnDefinitions(isGpBillingCenterEnabled),
    [isGpBillingCenterEnabled],
  );
  const { selectedCase, onClose: closeDrawer, onCellClicked } = useCaseSlideoutDrawer();

  const handleDrawerClose = useCallback(() => {
    closeDrawer();
    gridApi?.refreshServerSide({ purge: true });
  }, [closeDrawer, gridApi]);

  const checkerPendingIds = useMemo(
    () => (selectedCase ? [selectedCase.changeRequestId] : []),
    [selectedCase],
  );

  const dataSource = useMemo(() => dataSourceFactory(arenaId), [arenaId, dataSourceFactory]);

  useEffect(() => {
    updateGridDataSource(gridApi, dataSource);
  }, [gridApi, dataSource]);

  const handleOnGridReady = useCallback(
    (event: AgGrid.GridReadyEvent) => {
      setGridApi(event.api);
      updateGridDataSource(event.api, dataSource);
    },
    [dataSource],
  );

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    rowHeight: 48,
    suppressCellFocus: true,
    sideBar: sideBarDef,
    domLayout: "normal",
    rowModelType: "serverSide",
    getRowId,
    pagination: true,
    paginationPageSizeSelector: false,
    cacheBlockSize: PAGINATION.CASES_PAGE_SIZE,
    paginationPageSize: PAGINATION.CASES_PAGE_SIZE,
    onGridReady: handleOnGridReady,
    onCellClicked,
    defaultColDef: {
      flex: 1,
    },
  };

  return (
    <>
      <Flex flexGrow={1}>
        <Card className={billingCasesOverviewCard}>
          <Text variant="h3" className={billingCasesOverviewHeading}>
            Cases
          </Text>
          <Flex flexGrow={1}>
            <Grid
              gridOptions={gridOptions}
              columnDefs={colDefs}
              loadingCellRenderer={LoadingCellRenderer}
            />
          </Flex>
        </Card>
      </Flex>
      <CaseDetailsDrawer
        billingCase={selectedCase}
        onClose={handleDrawerClose}
        isChecker={isChecker}
        pendingChangeRequestIds={checkerPendingIds}
      />
    </>
  );
};
